'use client'

import { useTranslations } from 'next-intl'
import { useLayoutEffect, useRef, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { generateUUID } from '../../libs/uuid'

import Form from '../../form'
import useUpdateEffect from '../../hooks/update-effect'
import modalStore from '../../stores/modal-store'
import toastStore from '../../stores/toast-store'
import { Table as RadixTable, Button, Flex } from '@radix-ui/themes'

import { TableContextProvider, useTableContext, useTableStore } from '../context/table-context'
import { createTableStore } from '../store/table-store'
import { StyledTable, TableGlobalActionButtonContainer } from '../styled'
import { TTableProps } from '../types/table-props'
import TableBody from './table-body'
import TableEffects from './table-effects'
import { resolvePinnedFilter } from './table-filters-popover/pinned-filters'
import TableFiltersPopover from './table-filters-popover/table-filters-popover'
import TableFooter from './table-footer'
import TableGlobalActions from './table-global-actions'
import TableHead from './table-head'
import TableToggleablePopover from './table-toggleable-popover'
import { getCompleteFilters } from './table-utils'

type DataWithKey<T> = {
    [K in keyof T]: T[K]
}

const RenderControls = <T,>({ controls }: { controls: TTableProps<T>['controls'] }) => {
    const filters = useTableStore('filters')
    const refreshData = useTableStore('refreshData')
    const { store } = useTableContext<T>()

    if (!controls)
        return null

    return controls(filters, newFilters => store.setFilters(newFilters), refreshData)
}

const Table = <T extends DataWithKey<T>>({
    ref,
    data,
    columns,
    actions = [],
    formFields,
    formSubmitText,
    formWidth,
    create,
    update,
    createForm,
    createFormWidth,
    updateForm,
    getData,
    total,
    delete_,
    deleteSuccessMessage,
    deleteCondition,
    updateCondition,
    defaultPageSize = 25,
    expandable,
    hidePagination = false,
    selectable = false,
    header,
    controls,
    onRowSelected,
    onHiddenColumnsChanged,
    id = 'id' as keyof T,
    draggable = false,
    onReorder,
    classNameGenerator,
    globalActions,
    defaultHiddenColumns = [],
    hideHeaderRow = false,
    customRowRender,
    defaultFilters = [],
    saveFilters = true,
    tableActionsBreakpoint = 3,
    suppressSubmitMessage = false,
    generateRowProps,
    calculateSummary,
}: TTableProps<T>) => {
    const t = useTranslations('table')
    const tGeneric = useTranslations()

    const { pushNewModal, resetLastModal } = modalStore
    const { updateToast } = toastStore

    // refreshData is stored in the table store once, so it must read the *latest* getData
    // (otherwise a getData that closes over parent state keeps querying with stale values)
    const getDataRef = useRef(getData)
    useLayoutEffect(() => {
        getDataRef.current = getData
    })

    const tableActions = [...actions]
    if (delete_) {
        tableActions.push({
            label: t('delete'),
            action: async value => {
                await delete_(value)
                updateToast({
                    title: typeof deleteSuccessMessage === 'function'
                        ? deleteSuccessMessage(value)
                        : deleteSuccessMessage ?? t('record-deleted'),
                    type: 'success',
                })
            },
            name: 'delete',
            hidden: deleteCondition,
        })
    }

    if (update) {
        tableActions.push({
            label: t('edit'),
            action: row => getForm(update, row),
            hidden: updateCondition,
        })
    }

    const refreshDataGuard = useRef(0)
    const doRefreshData = async (clearSelectedRows: boolean) => {
        const getData = getDataRef.current
        if (getData === undefined)
            return

        const snapshot = store.get()

        ++refreshDataGuard.current
        const currentGuardValue = refreshDataGuard.current

        // unfinished filters (no column / operator / value yet) are not sent
        const filterValues = getCompleteFilters(snapshot.filters)
        const sorter = snapshot.sorter ?? undefined
        const { page, pageSize } = snapshot.pagination

        if (clearSelectedRows)
            store.update({ selectedRows: new Map() })

        store.update({ loading: true })

        try {
            const { data, total } = await getData({
                page,
                pageSize,
                filters: filterValues,
                sorter,
            })

            if (currentGuardValue == refreshDataGuard.current)
                store.update({ data, total })
        }
        catch {
            if (currentGuardValue == refreshDataGuard.current)
                store.update({ data: [], total: 0 })
        }

        if (currentGuardValue == refreshDataGuard.current)
            store.update({ loading: false })
    }

    // Several triggers can fire in the same commit (e.g. a filter change also resets the page).
    // Calls made in the same tick are merged into one request.
    const scheduledRefresh = useRef<{ clearSelectedRows: boolean; promise: Promise<void> } | null>(null)
    const refreshData = (clearSelectedRows: boolean = true): Promise<void> => {
        const scheduled = scheduledRefresh.current
        if (scheduled) {
            scheduled.clearSelectedRows ||= clearSelectedRows
            return scheduled.promise
        }

        const entry = { clearSelectedRows, promise: Promise.resolve() }
        entry.promise = Promise.resolve().then(() => {
            scheduledRefresh.current = null
            return doRefreshData(entry.clearSelectedRows)
        })
        scheduledRefresh.current = entry
        return entry.promise
    }

    const newTotal = total ?? data?.length ?? 0
    // lazy initial state: the store (uuid generation, filter reduction...) is built only once
    const [store] = useState(() => {
        const pinnedFilters = columns.filter(c => c.pinnedFilter).map(resolvePinnedFilter)
        return createTableStore<T>({
            data: data ?? [],
            total: newTotal,
            loading: data === undefined,
            hiddenColumns: defaultHiddenColumns,
            filters: Object.fromEntries(
                defaultFilters.map(filter => {
                    const pinned = pinnedFilters.find(p => p.column.split('|').includes(filter.column))
                    return [pinned?.key ?? generateUUID(), filter]
                })
            ),
            sorter: null,
            selectedRows: new Map(),
            pagination: { page: 0, pageSize: defaultPageSize },
            refreshData: refreshData,
        })
    })

    useUpdateEffect(() => {
        if (getData === undefined && data !== undefined) {
            store.update({
                data,
                total: newTotal,
                loading: false,
            })
        }
    }, [data, newTotal])

    const toggleableColumns: Set<string> = new Set(
        columns
            .map((col, idx) => ({ ...col, index: idx.toString() }))
            .filter(col => col.toggleable)
            .map(x => x.index)
    )

    const setRow = (newRowData: T | T[]) => store.setRow(newRowData, id)

    const getForm = (onSubmit: (data: FieldValues) => Promise<void | null | T | T[]>, data?: T) => {
        const handleSubmitInternal = async (data: FieldValues) => {
            try {
                const response = await onSubmit(data)
                resetLastModal()

                if (getData === undefined && typeof response === 'object' && response != null)
                    setRow(response)
                else
                    refreshData()

                if (!suppressSubmitMessage) {
                    updateToast({
                        title: t('saved'),
                        type: 'success',
                    })
                }

                return response
            }
            catch (error: any) {
                if ((error as unknown as Error).message?.startsWith('error.')) {
                    updateToast({
                        title: t('error-occured'),
                        message: tGeneric(error.message as any),
                        type: 'error',
                    })
                    return
                }

                console.error(error)
                updateToast({
                    title: t('unexpected-error-on-save'),
                    message: error.message,
                    type: 'error',
                })
            }
        }

        if (data === undefined && createForm) {
            const CreateForm = createForm
            pushNewModal({
                title: t('new-record'),
                onOpenChange: () => resetLastModal(),
                content: (
                    <CreateForm onSubmit={handleSubmitInternal} close={() => resetLastModal()} />
                ),
                width: createFormWidth ?? formWidth,
            })
            return
        }

        if (data && updateForm) {
            const UpdateForm = updateForm
            pushNewModal({
                title: t('edit-record'),
                content: (
                    <UpdateForm
                        onSubmit={handleSubmitInternal}
                        close={() => resetLastModal()}
                        data={structuredClone(data)}
                    />
                ),
                width: formWidth,
            })
            return
        }

        if (!formFields)
            return

        pushNewModal({
            title: data ? t('edit-record') : t('new-record'),
            content: (
                <Form
                    onSubmit={handleSubmitInternal}
                    onClose={() => resetLastModal()}
                    fields={formFields}
                    data={data}
                    submitText={formSubmitText}
                    resetOnSubmit={false}
                />
            ),
            width: formWidth,
        })
    }

    const canCreate =
        create !== undefined &&
        ((formFields !== undefined && formFields.length > 0) || createForm !== undefined)

    return (
        <TableContextProvider
            data={{
                store,
                defaultPageSize,
                serverSide: getData !== undefined,
                hidePagination,
                hideHeaderRow,
                draggable,
                expandable,
                columns,
                globalActions,
                saveFilters,
                toggleableColumns,
                selectable,
                id,
                classNameGenerator,
                customRowRender,
                tableActionsBreakpoint,
                generateRowProps,
                actions: tableActions,
                onRowSelected,
                onHiddenColumnsChanged,
                onReorder,
                calculateSummary,
            }}
        >
            <TableEffects ref={ref} />

            {header !== undefined && header()}

            <StyledTable>
                <TableGlobalActionButtonContainer>
                    {canCreate && (
                        <Button onClick={() => getForm(create!)} color="cyan" variant="soft">
                            {t('create')}
                        </Button>
                    )}
                    <TableGlobalActions />
                    <RenderControls controls={controls} />
                </TableGlobalActionButtonContainer>

                <Flex gap="0.5rem" align="end" mb="1">
                    {/* filtering is done by getData, static data has nothing to apply the filters */}
                    {getData !== undefined && <TableFiltersPopover />}
                    <TableToggleablePopover />
                </Flex>

                <RadixTable.Root>
                    <TableHead />
                    <TableBody />
                </RadixTable.Root>

                <TableFooter />
            </StyledTable>
        </TableContextProvider>
    )
}

export default Table
